import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Divider, Empty, Spin, Typography } from "antd";
import { getSnippetByLanguage } from "../../../../Utils/language-snippets";
import Editor from "@monaco-editor/react";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import { ProgrammingLanguage } from "../../../../Interface/Index";
import { AIAssessmentQuestion } from "../../../../Entities/ai-assessment.entity";
import AiAssessmentCodeEditorHeader from "./AiAssessmentCodeEditorHeader";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentQuestionNavigation from "../../hooks/useAiAssessmentQuestionNavigation";
import codeService from "../../../../Services/code.service";

const PLACEHOLDER_TEXT = "Write your code solution here...";

const judge0ToMonacoLanguageMap: { [key: string]: string } = {
  "Assembly (NASM 2.14.02)": "asm",
  "Bash (5.0.0)": "shell",
  "Basic (FBC 1.07.1)": "vb",
  "C (Clang 7.0.1)": "c",
  "C++ (Clang 7.0.1)": "cpp",
  "C (GCC 7.4.0)": "c",
  "C++ (GCC 7.4.0)": "cpp",
  "C (GCC 8.3.0)": "c",
  "C++ (GCC 8.3.0)": "cpp",
  "C (GCC 9.2.0)": "c",
  "C++ (GCC 9.2.0)": "cpp",
  "Clojure (1.10.1)": "clojure",
  "C# (Mono 6.6.0.161)": "csharp",
  "COBOL (GnuCOBOL 2.2)": "cobol",
  "Common Lisp (SBCL 2.0.0)": "lisp",
  "D (DMD 2.089.1)": "d",
  "Elixir (1.9.4)": "elixir",
  "Erlang (OTP 22.2)": "erlang",
  "F# (.NET Core SDK 3.1.202)": "fsharp",
  "Fortran (GFortran 9.2.0)": "fortran",
  "Go (1.13.5)": "go",
  "Groovy (3.0.3)": "groovy",
  "Haskell (GHC 8.8.1)": "haskell",
  "Java (OpenJDK 13.0.1)": "java",
  "JavaScript (Node.js 12.14.0)": "javascript",
  "Kotlin (1.3.70)": "kotlin",
  "Lua (5.3.5)": "lua",
  "Objective-C (Clang 7.0.1)": "objective-c",
  "OCaml (4.09.0)": "ocaml",
  "Octave (5.1.0)": "octave",
  "Pascal (FPC 3.0.4)": "pascal",
  "Perl (5.28.1)": "perl",
  "PHP (7.4.1)": "php",
  "Plain Text": "plaintext",
  "Prolog (GNU Prolog 1.4.5)": "prolog",
  "Python (2.7.17)": "python",
  "Python (3.8.1)": "python",
  "R (4.0.0)": "r",
  "Ruby (2.7.0)": "ruby",
  "Rust (1.40.0)": "rust",
  "Scala (2.13.2)": "scala",
  "SQL (SQLite 3.27.2)": "sql",
  "Swift (5.2.3)": "swift",
  "TypeScript (3.7.4)": "typescript",
  "Visual Basic.Net (vbnc 0.0.0.5943)": "vb",
  "Multi-file program": "plaintext",
  Executable: "plaintext",
};

function getMonacoLanguage(judge0LangName: string) {
  return judge0ToMonacoLanguageMap[judge0LangName] || "plaintext";
}

interface AiAssessmentCodeEditorProps {
  languages: ProgrammingLanguage[];
  setCompiling: (compiling: { loading: boolean; message: string }) => void;
  handleCodingFullscreenToggle: () => void;
  output: string | null;
  setOutput: (output: string | null) => void;
  isCodingFullscreen: boolean;
  isCompiling: { loading: boolean; message: string };
}

const AiAssessmentCodeEditor: React.FC<AiAssessmentCodeEditorProps> = ({
  languages,
  setCompiling,
  handleCodingFullscreenToggle,
  isCodingFullscreen,
  output,
  setOutput,
  isCompiling,
}) => {
  const { saveTemporaryAnswer } = useAiAssessmentQuestionNavigation();
  const { currentStep, aiAssessmentQuestions, temporaryAnswer } =
    useAIAssessmentContext();

  const question = aiAssessmentQuestions[currentStep];
  const [selectedLanguage, setSelectedLanguage] =
    useState<ProgrammingLanguage | null>(null);
  const [code, setCode] = useState<string>(
    question.answer || temporaryAnswer || ""
  );
  const [showOutputAlert, setShowOutputAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState<string>("Note");
  const [alertDescription, setAlertDescription] = useState<string>(
    "You can still submit your code even if testing shows errors. Your solution will be evaluated based on your code logic."
  );

  const programmaticChange = useRef(false);
  const prevQuestionRef = useRef<AIAssessmentQuestion | null>(null);

  const onMonacoChange = (value: string | undefined) => {
    const safeValue = value ?? "";
    if (programmaticChange.current) {
      programmaticChange.current = false;
      setCode(safeValue);
      return;
    }
    setCode(safeValue);
    saveTemporaryAnswer(safeValue);
  };

  const handleSelectLanguage = (language: ProgrammingLanguage | null) => {
    if (!language) {
      setSelectedLanguage(null);
      setCode("");
      return;
    }

    setSelectedLanguage(language);
    
    // Only set snippet if not Plain Text and no existing code
    if (language.name.toLowerCase() !== "plain text" && !code.trim()) {
      const snippet = getSnippetByLanguage(language.name);
      setCode(snippet);
    } else if (language.name.toLowerCase() === "plain text") {
      // Don't clear existing code when switching to Plain Text
      // Allow user to keep their code
    }
  };

  const handleRunCodeClick = async () => {
    if (!selectedLanguage) return;

    // Don't allow testing Plain Text
    if (selectedLanguage.name.toLowerCase() === "plain text") {
      setOutput("Please select a programming language to test your code.");
      setAlertTitle("Language Required");
      setAlertDescription("Please select a programming language to test your code.");
      setShowOutputAlert(true);
      return;
    }

    if (!code.trim()) {
      setOutput("Please write some code before running.");
      setAlertTitle("Code Required");
      setAlertDescription("Please write some code before running.");
      setShowOutputAlert(true);
      return;
    }

    setCompiling({ loading: true, message: "Running code..." });
    setShowOutputAlert(false);

    try {
      // Use structured format for better error handling
      const result = await codeService.compileCode(
        code,
        selectedLanguage.id,
        true // Use structured format
      );

      // Handle both formats for backward compatibility
      if (typeof result === "string") {
        // Old format: string
        setOutput(result || "Code executed successfully.");
      } else if (result === null) {
        // Old format: null on error
        setOutput(
          "An error occurred while running your code. You can still submit your code and it will be considered for evaluation."
        );
        setAlertTitle("Execution Error");
        setAlertDescription(
          "An error occurred while running your code. You can still submit your code and it will be considered for evaluation."
        );
        setShowOutputAlert(true);
      } else {
        // New structured format
        if (result.success) {
          setOutput(result.output || "Code executed successfully.");
        } else {
          // Show user-friendly error message with title and description
          setOutput(result.output || "An error occurred.");
          setAlertTitle(result.errorTitle || "Execution Error");
          setAlertDescription(
            result.errorDescription ||
              "An error occurred while running your code. You can still submit your code and it will be considered for evaluation."
          );
          setShowOutputAlert(true);
        }
      }
    } catch (error: any) {
      setOutput(
        "Unable to test code at this time. You can still submit your code and it will be considered for evaluation."
      );
      setAlertTitle("Service Unavailable");
      setAlertDescription(
        "Unable to test code at this time. You can still submit your code and it will be considered for evaluation."
      );
      setShowOutputAlert(true);
    } finally {
      setCompiling({ loading: false, message: "" });
    }
  };

  useEffect(() => {
    if (
      question.questionId &&
      question.questionId !== prevQuestionRef.current?.questionId
    ) {
      prevQuestionRef.current = question;
      setSelectedLanguage(null);
      setOutput(null);
      setShowOutputAlert(false);
      setAlertTitle("Note");
      setAlertDescription(
        
        "You can still submit your code even if testing shows errors. Your solution will be evaluated based on your code logic."
      );

      const initialCode = question.answer;

      if (initialCode && initialCode.trim()) {
        setCode(initialCode);
        setTimeout(() => {
          const syntheticEvent = {
            target: { value: initialCode },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          // onChange(syntheticEvent, initialCode);
        }, 0);
      } else {
        setCode("");
        setTimeout(() => {
          const syntheticEvent = {
            target: { value: "" },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          // onChange(syntheticEvent, "");
        }, 0);
      }
    }
  }, [question]);

  return (
    <div className="ai-assessment-code-editor-container">
      <div>
        <AiAssessmentCodeEditorHeader
          languages={languages}
          handleSelectLanguage={handleSelectLanguage}
          selectedLanguage={selectedLanguage}
          handleCodingFullscreenToggle={handleCodingFullscreenToggle}
          isCodingFullscreen={isCodingFullscreen}
        />
      </div>

      <div className="ai-assessment-code-editor-section">
        {isCompiling.loading && (
          <div className="loader-overlay">
            <div className="loader-content">
              <Spin
                size="large"
                indicator={<Loading3QuartersOutlined spin />}
              />
              <span className="loader-text">Running code...</span>
            </div>
          </div>
        )}

        <Editor
          value={code}
          theme="vs-dark"
          language={
            selectedLanguage
              ? getMonacoLanguage(selectedLanguage.name)
              : "plaintext"
          }
          height={
            isCodingFullscreen
              ? "calc(var(--real-vh, 1vh) * 100 - 380px)"
              : "calc(var(--real-vh, 1vh) * 100 - 500px)"
          }
          width="100%"
          options={{
            readOnly: isCompiling.loading,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: "on",
            placeholder: PLACEHOLDER_TEXT,
            domReadOnly: isCompiling.loading,
            contextmenu: false,
            hover: { enabled: false },
            parameterHints: { enabled: false },
            inlayHints: { enabled: "off" },
          }}
          onChange={onMonacoChange}
        />
      </div>

      <div className="ai-assessment-code-editor-output">
        <div className="ai-assessment-section-title">
          <Typography.Title
            level={5}
            style={{ margin: "0px", fontSize: "14px" }}
          >
            Test Output (Optional)
          </Typography.Title>

          <Button
            type="primary"
            disabled={
              !selectedLanguage ||
              isCompiling.loading ||
              !code.trim()
            }
            loading={isCompiling.loading}
            onClick={handleRunCodeClick}
          >
            Test Code
          </Button>
        </div>
        <Divider style={{ margin: "0px" }} />

        <div>
          {showOutputAlert && output && !output.includes("successfully") && (
            <Alert
              message={alertTitle}
              description={alertDescription}
              type="info"
              showIcon
              closable
              onClose={() => setShowOutputAlert(false)}
              style={{ marginBottom: 12 }}
            />
          )}

          {output ? (
            <div className="output-container">
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  fontSize: "14px",
                  lineHeight: "1.4",
                }}
              >
                {output}
              </pre>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Test output will appear here (optional)"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AiAssessmentCodeEditor;
